import { getWidgetBaseUrl } from "./origin";
import type { WidgetState } from "./state";

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[c]!;
  });
}

const uploadIcon = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 16V4"/>
    <path d="m6 10 6-6 6 6"/>
    <path d="M4 20h16"/>
  </svg>
`;

const spinner = `<div class="spinner" role="status" aria-label="Loading"></div>`;

function renderStatus(label: string, sub?: string): string {
  return `
    <div class="status">
      ${spinner}
      <div class="status-label">${escapeHtml(label)}</div>
      ${sub ? `<div class="status-sub">${escapeHtml(sub)}</div>` : ""}
    </div>
  `;
}

function renderProgress(label: string, percent: number): string {
  const safe = Math.max(0, Math.min(100, Math.round(percent)));
  return `
    <div class="progress-wrap">
      <div class="status-label">${escapeHtml(label)}</div>
      <div class="progress"><div style="width:${safe}%"></div></div>
      <div class="progress-meta">
        <span>Please don't close this window</span>
        <strong>${safe}%</strong>
      </div>
    </div>
  `;
}

export function render(state: WidgetState): string {
  switch (state.kind) {
    case "initial":
      return `
        <div class="dropzone" data-dropzone>
          <div class="dropzone-icon">${uploadIcon}</div>
          <div class="dropzone-label">Drop your video here or click to browse</div>
          <div class="dropzone-hint">MP4 or MOV &bull; up to 500MB &bull; max 5 minutes</div>
          <input type="file" accept="video/mp4,video/quicktime" data-file-input style="display:none">
        </div>
      `;
    case "validating":
      return renderStatus("Checking your video...");
    case "selected":
      return `
        <div class="preview-wrap">
          <video class="preview-video" src="${escapeHtml(state.previewUrl)}" controls playsinline></video>
          <div class="button-row">
            <button class="cta cta--ghost" data-delete-cta type="button">Remove</button>
            <button class="cta" data-upload-cta type="button">Upload video</button>
          </div>
        </div>
      `;
    case "authenticating":
      return renderStatus("Starting your session...");
    case "creatingProject":
      return renderStatus("Preparing upload...");
    case "uploading":
      return renderProgress(`Uploading ${state.file.name}`, state.progress);
    case "processing":
      return renderProgress("Adding captions to your video", state.progress);
    case "previewReady": {
      const previewSrc = `${getWidgetBaseUrl()}/anonymous-preview/${state.projectId}`;
      return `
        <div class="preview-wrap">
          <iframe src="${previewSrc}" class="preview-video" allow="autoplay" allowfullscreen></iframe>
          <button class="cta" data-edit-cta data-project-id="${state.projectId}">
            Edit your video now
          </button>
          <div class="dropzone-hint">Create a free account to access the editor</div>
        </div>
      `;
    }
    case "error":
      return `
        <div class="error">
          <div class="error-message">${escapeHtml(state.message)}</div>
          <button class="cta cta--ghost" data-retry>Try again</button>
        </div>
      `;
  }
}
